import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API, authHeaders } from "@/lib/api";

/**
 * Turn CC · Sub-Phase 1b — Direct Google sign-in.
 *
 * The Emergent-managed OAuth exchange has been removed. This page is the
 * single entry point into the app: it either bounces an already-authenticated
 * user straight to the dashboard, or renders one dark, deliberately spare
 * "Continue with Google" button that hands off to the backend
 * `/api/oauth/google/login?kind=signin` endpoint.
 */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [oauthError, setOauthError] = useState(null);

  useEffect(() => {
    // Surface any error string the backend appended to the redirect
    // (e.g. `/login?oauth_error=access_denied`).
    const err = new URLSearchParams(location.search).get("oauth_error");
    if (err) setOauthError(err);
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { headers: authHeaders() });
        if (cancelled) return;
        if (res.ok) {
          navigate("/", { replace: true });
          return;
        }
      } catch {
        /* offline — fall through to the button */
      }
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const startLogin = () => {
    // Full-page redirect so the browser follows the 302 → Google consent.
    window.location.assign(`${API}/oauth/google/login?kind=signin`);
  };

  if (checking) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center"
        style={{ background: "#0A0A0B" }}
      >
        <div
          className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em]"
          style={{
            color: "rgba(237,237,237,0.5)",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          }}
        >
          <span
            className="h-2 w-2 rounded-full animate-pulse"
            style={{
              background: "#7FFFC4",
              boxShadow: "0 0 14px rgba(127,255,196,0.55)",
            }}
          />
          resolving session…
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% -10%, rgba(127,255,196,0.05), transparent 60%), #0A0A0B",
        color: "#EDEDED",
      }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-10">
          <span
            className="h-2 w-2 rounded-full"
            style={{
              background: "#7FFFC4",
              boxShadow: "0 0 12px rgba(127,255,196,0.55)",
            }}
          />
          <span
            className="text-[11px] uppercase tracking-[0.28em]"
            style={{
              color: "rgba(237,237,237,0.55)",
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            }}
          >
            hail_mary_os
          </span>
        </div>

        <h1
          className="text-4xl sm:text-5xl font-semibold leading-[1.05] tracking-tight"
          style={{ color: "#EDEDED" }}
          data-testid="login-title"
        >
          Hail Mary
          <br />
          <span style={{ color: "#7FFFC4" }}>Intelligence OS</span>
        </h1>

        <p
          className="mt-5 text-sm leading-relaxed max-w-sm"
          style={{ color: "rgba(237,237,237,0.68)" }}
        >
          The agentic intelligence layer powering Beyond Activ.
        </p>

        <button
          type="button"
          onClick={startLogin}
          data-testid="google-signin-btn"
          className="mt-10 w-full h-12 rounded-xl flex items-center justify-center gap-3 transition-colors group"
          style={{
            background: "#111114",
            border: "1px solid rgba(255,255,255,0.10)",
            color: "#EDEDED",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#17171b";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#111114";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
          }}
        >
          <GoogleGlyph />
          <span className="text-[15px] font-medium tracking-tight">
            Continue with Google
          </span>
        </button>

        <p
          className="mt-4 text-[11px] text-center"
          style={{ color: "rgba(237,237,237,0.38)" }}
          data-testid="login-hint"
        >
          Sign in with your Beyond Activ Google account.
        </p>

        {oauthError && (
          <div
            data-testid="login-oauth-error"
            className="mt-6 rounded-lg px-4 py-3 text-xs"
            style={{
              background: "rgba(232,90,90,0.08)",
              border: "1px solid rgba(232,90,90,0.35)",
              color: "#F4B4B4",
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            }}
          >
            oauth_error: {oauthError}
          </div>
        )}
      </div>
    </div>
  );
}

function GoogleGlyph() {
  // Official Google "G" — multicolor SVG.
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}
